/* ============================================================
   Comparing two corridor runs.

   Every tool in this product answers one run in isolation. The question
   an operator actually asks is comparative — this lane or that one,
   road or rail, this quarter against last. The engine already computes
   each side; nothing put two of them next to each other.

   Two ideas carry the whole module.

   1. A number that moved is not a finding. "0.071 → 0.064" is data.
      Whether it matters depends on how far it moved relative to what
      the inputs can support, and that is graded here on its own scale
      rather than borrowed from the breaking-point engine.

      This module originally did borrow it, on the argument that
      fragility() reads the same axis from the other end. It does not:
      fragility grades HEADROOM, a fraction of a base that lives in
      0–1, and movement has no upper bound. Squeezing one into the
      other made a doubling read as "Slight". The two questions are
      genuinely different — how close is this to breaking, versus how
      far has this travelled — and they get two sets of thresholds,
      each stated where it is used.

   2. Two runs are only comparable if they were measured the same way.
      A run priced at 28 t against one priced at 20 t differs in cost
      per tonne-km for reasons that have nothing to do with the lane —
      the same trip cost spread over more tonnes. Reporting that as a
      lane finding would be the same class of error as the Infinity
      DSCR: arithmetically correct, and a lie about the business.

   On (2) the response is deliberately NOT to refuse the comparison.
   Refusing hides a comparison the reader may have good reason to want,
   and silently normalising invents a run nobody performed. Instead the
   differences that distort the reading are named, in the output, next
   to the numbers they distort. The reader decides. That is the same
   posture as "(no published source)" on a benchmark and "3 domains
   deliberately carry none" on the scorecard.
   ============================================================ */


/* Below this, a payload difference is rounding rather than a different
   operation. Above it, cost per tonne-km moves for utilisation reasons
   and the reader has to be told before they read it as a lane result. */
export const PAYLOAD_TOLERANCE = 0.1;

/* A move smaller than this is inside the precision the inputs support.
   Trip costs are entered to the nearest dollar on figures in the
   thousands; treating a 0.4% shift as a result would be reading noise
   as signal. */
export const NOISE_FLOOR = 0.01;

/* How big a move has to be before it is worth a reader's attention.

   These are NOT fragility()'s thresholds, and the first version of this
   file used them anyway. The comment justifying that read:

     "fragility() ranks by how SMALL a move is ... Here the reading is
      inverted: a large move is the notable one. Same scale, read from
      the other end, so the two cannot drift apart."

   It was elegant and it was wrong. fragility() is calibrated for
   HEADROOM — a fraction of a base that lives in 0–1, where 0.1 means
   "ten per cent away from breaking". Feeding it 1/(1+move) does map
   movement into that range, but it compresses it savagely: a metric
   that DOUBLES only reaches 0.5, which is deep inside "info". In
   practice /compare labelled a 100% rise in cost per tonne-km "Slight",
   and reserved "Material" for a move of a thousand per cent. Nothing in
   the test suite noticed, because the assertions covered the noise
   floor and nothing above it.

   Two different questions need two different scales. Headroom asks how
   close something is to breaking; movement asks how far it travelled.
   The numbers below are for corridor economics: a fifth is a decision,
   a twentieth is a fact, and between them is a thing worth explaining. */
export const NOTABLE_MOVE = 0.05;
export const MATERIAL_MOVE = 0.2;

/**
 * Severity of a movement, by how far it moved.
 * @param {number} move absolute fractional change
 */
export function movementSeverity(move) {
  if (!Number.isFinite(move) || move < NOISE_FLOOR) return 'none';
  if (move >= MATERIAL_MOVE) return 'danger';
  if (move >= NOTABLE_MOVE) return 'warning';
  return 'info';
}

/**
 * What makes these two runs not like-for-like.
 * Returns [] when they are directly comparable.
 * @returns {Array<{code: string, note: string}>}
 */
export function comparability(a, b) {
  const caveats = [];
  if (!a || !b) return caveats;

  if (a.schemaVersion !== b.schemaVersion) {
    caveats.push({
      code: 'schema',
      note:
        `These runs were saved under different versions of the model ` +
        `(v${a.schemaVersion} and v${b.schemaVersion}). Fields may not mean the same thing in both.`
    });
  }

  const pa = a.input?.payload;
  const pb = b.input?.payload;
  if (pa && pb) {
    const spread = Math.abs(pa - pb) / Math.max(pa, pb);
    if (spread > PAYLOAD_TOLERANCE) {
      caveats.push({
        code: 'payload',
        note:
          `Payloads differ — ${pa} t against ${pb} t. Cost per tonne-km spreads the ` +
          `trip over the tonnes carried, so part of any gap here is utilisation rather ` +
          `than the lane.`
      });
    }
  }

  // Different modes and different corridors are the comparisons this
  // feature exists for, so neither is a caveat. Mode is noted only
  // because a rail figure that is line-haul only is not the whole
  // journey, and that trap is easy to walk into unwarned.
  const ma = a.input?.mode;
  const mb = b.input?.mode;
  if (ma && mb && ma !== mb && (ma === 'rail' || mb === 'rail')) {
    caveats.push({
      code: 'mode',
      note:
        'One of these is rail. Published rail bands are line-haul only — they exclude ' +
        'first and last mile, lift charges and ICD storage. Add drayage at both ends ' +
        'before treating the gap as money.'
    });
  }

  return caveats;
}

/* Each metric states its own direction of good, because "up" is not
   universally better and a comparison that assumes it will congratulate
   an operator on rising costs. */
const METRICS = [
  {
    key: 'costPerTonKm',
    label: 'Cost per tonne-km',
    unit: 'USD',
    better: 'lower',
    read: (r) => r.cost?.costPerTonKm
  },
  {
    key: 'marginPct',
    label: 'Margin',
    unit: '%',
    better: 'higher',
    read: (r) => r.margin?.marginPct
  },
  {
    key: 'profit',
    label: 'Profit on the run',
    unit: 'USD',
    better: 'higher',
    read: (r) => r.margin?.profit
  },
  {
    key: 'demurrage',
    label: 'Demurrage exposure',
    unit: 'USD',
    better: 'lower',
    read: (r) => r.demurrage?.total
  },
  {
    key: 'intensity',
    label: 'Carbon intensity',
    unit: 'g/tonne-km',
    better: 'lower',
    read: (r) => r.carbon?.intensity
  }
];

/**
 * Compare two assessed runs.
 * @param {object} a the earlier run
 * @param {object} b the later run
 */
export function compareRuns(a, b) {
  const caveats = comparability(a, b);
  const deltas = [];

  for (const m of METRICS) {
    const from = m.read(a);
    const to = m.read(b);
    if (!Number.isFinite(from) || !Number.isFinite(to)) continue;

    const change = to - from;

    /* A move from zero has no meaningful percentage — 0 to 40 is not
       "infinity percent worse", it is the appearance of an exposure
       that was not there. Say that instead of printing a symbol. */
    const fromZero = from === 0;
    const move = fromZero ? null : Math.abs(change) / Math.abs(from);

    let severity;
    if (fromZero) {
      // An exposure that was not there and now is. There is no
      // percentage to grade, and its arrival is the finding.
      severity = change === 0 ? 'none' : 'danger';
    } else {
      severity = movementSeverity(move);
    }

    const improved = change === 0 ? null : m.better === 'lower' ? change < 0 : change > 0;

    deltas.push({
      key: m.key,
      label: m.label,
      unit: m.unit,
      from,
      to,
      change,
      movePct: move === null ? null : move * 100,
      fromZero,
      improved,
      severity
    });
  }

  /* Order by how much it moved, not by which metric someone thought was
     important. The reader is scanning for what changed. */
  const ranked = [...deltas].sort((x, y) => {
    const mx = x.movePct === null ? Infinity : x.movePct;
    const my = y.movePct === null ? Infinity : y.movePct;
    return my - mx;
  });

  const material = ranked.filter((d) => d.severity !== 'none');

  return {
    caveats,
    deltas: ranked,
    material,
    /* Stated rather than left for the caller to infer from an empty
       array, because "nothing moved" is a real and useful answer and it
       should not look like a failure to compute anything. */
    unchanged: material.length === 0
  };
}

/** Human summary line. Kept here so the wording cannot drift per view. */
export function summarise(result) {
  if (result.unchanged) {
    return 'No material change between these runs.';
  }
  const worse = result.material.filter((d) => d.improved === false).length;
  const better = result.material.filter((d) => d.improved === true).length;
  const parts = [];
  if (better) parts.push(`${better} improved`);
  if (worse) parts.push(`${worse} worse`);
  return parts.join(', ');
}
